import { Badge } from '@/components/ui/badge';
import { ContentContainer } from '@/components/ui/content-container';
import { Heading2, Heading3, Paragraph } from '@/components/ui/typography';
import { formatProfileNames, normalizeProfiles } from '../../profile-list';
import type { CaseResultWidgetProps } from '../definition';

export function CardsVariant({ title, caseResults }: CaseResultWidgetProps) {
  if (caseResults.length === 0) return null;

  return (
    <section className="bg-muted">
      <ContentContainer as="div">
        <div className="mb-12 text-center">
          <Heading2 className="mb-4">{title}</Heading2>
          <div className="flex items-center justify-center gap-2 mx-auto">
            <div className="h-[2px] w-8 bg-accent rounded-full" />
            <div className="h-[2px] w-24 bg-primary rounded-full" />
            <div className="h-[2px] w-8 bg-accent rounded-full" />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {caseResults.map((caseResult, index) => {
            const attorneys = normalizeProfiles(caseResult.attorneys);
            const attorneyLabel = formatProfileNames(attorneys);
            const courts = caseResult.courts ?? [];

            return (
              <article
                key={caseResult.id || index}
                className="group/case-item relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-lg transition-all hover:shadow-lg hover:-translate-y-1"
              >
                <div className="relative flex flex-1 flex-col p-5">
                  {caseResult.title && (
                    <Heading3 className="mb-2 text-lg font-bold group-hover/case-item:text-primary transition-colors line-clamp-2">
                      {caseResult.title}
                    </Heading3>
                  )}
                  {caseResult.summary && (
                    <Paragraph className="mb-3 flex-1 text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                      {caseResult.summary}
                    </Paragraph>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    {courts.map((court) => (
                      <Badge
                        key={court.id}
                        variant="outline"
                        colorScheme="secondary"
                        className="text-xs border-input"
                      >
                        {court.name}
                      </Badge>
                    ))}
                    {caseResult.practiceAreas &&
                      caseResult.practiceAreas.length > 0 &&
                      caseResult.practiceAreas.map((area) => (
                        <Badge
                          key={area.id || area.name}
                          variant="outline"
                          colorScheme="primary"
                          className="text-xs"
                        >
                          {area.name}
                        </Badge>
                      ))}
                  </div>
                  {attorneyLabel && (
                    <div className="flex items-center gap-1.5 pt-3 border-t border-border text-xs font-medium text-muted-foreground">
                      <span>{attorneys.length > 1 ? 'Attorneys:' : 'Attorney:'}</span>
                      <span>{attorneyLabel}</span>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </ContentContainer>
    </section>
  );
}
